// MobileRequestFlow — orchestrates the mobile request experience: the
// "Browse the catalog" sheet, a floating basket pill once items are added, and
// the configure sheet reached by tapping the pill. All state comes from the
// same hooks the desktop modal uses — this is presentation only.

import { Box, Portal } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import type { CatalogBrowse } from "../useCatalogBrowse";
import type { Basket } from "../useBasket";
import type { RecipientSelection } from "../useRecipientSelection";
import type { IdentitySelection } from "../useIdentitySelection";
import MobileBasketFab from "./MobileBasketFab";
import MobileCatalogSheet from "./MobileCatalogSheet";
import MobileConfigureSheet from "./MobileConfigureSheet";

const MobileRequestFlow = ({
  onClose,
  catalog,
  basket,
  selection,
  identity,
  onSubmit,
  isSubmitting,
  canSubmit,
  submitHint,
  activeResourceIds,
}: {
  onClose: () => void;
  catalog: CatalogBrowse;
  basket: Basket;
  selection: RecipientSelection;
  identity: IdentitySelection;
  onSubmit: () => void;
  isSubmitting: boolean;
  canSubmit: boolean;
  submitHint?: string | null;
  /** resourceIds the member already holds active access to (badges the cards). */
  activeResourceIds?: Set<string>;
}) => {
  const [stage, setStage] = useState<"browse" | "configure">("browse");
  // basketItems, not the raw id list: it's the deduped set that will actually
  // be requested (one row per selectable resource).
  const count = basket.basketItems.length;

  // If the basket empties mid-configure, drop back to browsing.
  useEffect(() => {
    if (stage === "configure" && count === 0) setStage("browse");
  }, [stage, count]);

  return (
    <>
      <MobileCatalogSheet
        isOpen={stage === "browse"}
        onClose={onClose}
        catalog={catalog}
        basket={basket}
        activeResourceIds={activeResourceIds}
      />
      <MobileConfigureSheet
        isOpen={stage === "configure"}
        onClose={() => setStage("browse")}
        basket={basket}
        selection={selection}
        identity={identity}
        onSubmit={onSubmit}
        isSubmitting={isSubmitting}
        canSubmit={canSubmit}
        submitHint={submitHint}
      />
      {stage === "browse" && count > 0 && (
        <Portal>
          <Box
            position="fixed"
            right="16px"
            bottom="calc(24px + env(safe-area-inset-bottom))"
            zIndex={1600}
          >
            <MobileBasketFab count={count} onClick={() => setStage("configure")} />
          </Box>
        </Portal>
      )}
    </>
  );
};

export default MobileRequestFlow;
